const ANIMATION_DURATION = 350
const ANIMATION_EASING = 'ease-out'

// native <details>/<summary> can't be animated with CSS alone: the browser
// toggles the content between "display: none" and its real display value in
// the same instant the "open" attribute changes, and a CSS transition has no
// valid "before" state to interpolate from across that jump — it just snaps.
// This mirrors the well-known Web Animations API pattern for animating
// <details> open/close (see web.dev "Building an expand/collapse animation"),
// adapted for this project's markup: instead of the whole <details>, only
// ".accordion__element-content" (everything below the toggle row) animates,
// since the toggle row itself shouldn't move.
class SmoothAccordionElement {
    constructor(detailsEl) {
        this.details = detailsEl
        this.summary = detailsEl.querySelector(':scope > summary')
        this.content = detailsEl.querySelector(':scope > .accordion__element-content')
        this.animation = null
        this.isClosing = false
        this.isExpanding = false

        if (!this.summary || !this.content) {
            return
        }

        this.summary.addEventListener('click', (event) => this.onClick(event))
    }

    onClick(event) {
        event.preventDefault()
        this.details.style.overflow = 'hidden'

        if (this.isClosing || !this.details.open) {
            this.open()
        } else if (this.isExpanding || this.details.open) {
            this.shrink()
        }
    }

    shrink() {
        this.isClosing = true

        const startHeight = `${this.content.offsetHeight}px`
        const endHeight = '0px'

        if (this.animation) {
            this.animation.cancel()
        }

        this.animation = this.content.animate(
            { height: [startHeight, endHeight] },
            { duration: ANIMATION_DURATION, easing: ANIMATION_EASING }
        )

        this.animation.onfinish = () => this.onAnimationFinish(false)
        this.animation.oncancel = () => { this.isClosing = false }
    }

    open() {
        this.content.style.height = '0px'
        this.details.open = true

        window.requestAnimationFrame(() => this.expand())
    }

    expand() {
        this.isExpanding = true

        const startHeight = '0px'
        const endHeight = `${this.content.scrollHeight}px`

        if (this.animation) {
            this.animation.cancel()
        }

        this.animation = this.content.animate(
            { height: [startHeight, endHeight] },
            { duration: ANIMATION_DURATION, easing: ANIMATION_EASING }
        )

        this.animation.onfinish = () => this.onAnimationFinish(true)
        this.animation.oncancel = () => { this.isExpanding = false }
    }

    onAnimationFinish(open) {
        this.details.open = open
        this.animation = null
        this.isClosing = false
        this.isExpanding = false
        this.details.style.overflow = ''
        this.content.style.height = ''
    }
}

export default function InitAccordion() {
    // editors need every accordion element visible and editable at once —
    // the Neos backend already forces that via CSS (".neos-backend" rules
    // in Accordion.scss); animating collapse/expand there would fight that
    if (window.neos || document.body.classList.contains('neos-backend')) {
        return
    }

    document.querySelectorAll('.accordion__element').forEach((detailsEl) => {
        new SmoothAccordionElement(detailsEl)
    })
}
